import moment from "moment";

/**
 * Pure helpers extracted from StorefrontOrdersList.vue so they can be
 * unit-tested under vitest (which can't mount Vuetify 3 SFCs in this project).
 */

/**
 * Format an ISO 8601 timestamp for display in the past-orders table.
 * Matches the existing pattern in OrderList.vue (Admin order list).
 *
 * @param {string|null|undefined} iso - ISO 8601 timestamp
 * @returns {string} formatted date "YYYY-MM-DD hh:mm A" or "" if missing
 */
export function formatOrderDate(iso) {
  if (!iso) return "";
  return moment(iso).format("YYYY-MM-DD hh:mm A");
}

/**
 * Format a dollar amount for display.
 *
 * Order.to_dict() emits `total` as dollars (a Python float rounded to 2
 * decimals — see api/src/models/order.py:55-62 and :171). We accept whatever
 * the backend hands us (number, numeric string, null) and emit "$XX.XX".
 *
 * @param {number|string|null|undefined} amount
 * @returns {string} formatted "$XX.XX"
 */
export function formatOrderTotal(amount) {
  const n = amount == null ? 0 : Number(amount);
  const safe = Number.isFinite(n) ? n : 0;
  return `$${safe.toFixed(2)}`;
}

/**
 * Extract the order's resource ID from a jsonapi-vuex-normalized object.
 *
 * jsonapi-vuex puts the JSON:API resource id at `order._jv.id`, NOT at
 * `order.id` (because JSON:API's `id` is a resource-object key, not an
 * attribute). A bare `order.id` lookup in templates returns undefined.
 *
 * This helper:
 *   1. Prefers `_jv.id` (canonical)
 *   2. Falls back to top-level `id` (defensive — handles non-jsonapi-vuex
 *      sources or shapes where the backend echoes id into attributes)
 *   3. Returns null if neither path is present
 *
 * @param {object|null|undefined} order
 * @returns {string|number|null}
 */
export function getOrderId(order) {
  if (!order || typeof order !== "object") return null;
  const jvId = order._jv && order._jv.id != null ? order._jv.id : null;
  if (jvId != null) return jvId;
  if (order.id != null) return order.id;
  return null;
}

/**
 * Paginate an array of orders client-side.
 *
 * @param {Array} orders
 * @param {number} page - 1-indexed
 * @param {number} perPage
 * @returns {{ items: Array, totalPages: number, page: number }}
 */
export function paginateOrders(orders, page, perPage) {
  const list = Array.isArray(orders) ? orders : [];
  const size = Math.max(1, perPage | 0);

  if (list.length === 0) {
    return { items: [], totalPages: 0, page: 1 };
  }

  const totalPages = Math.max(1, Math.ceil(list.length / size));
  let safePage = page | 0;
  if (safePage < 1 || safePage > totalPages) {
    safePage = 1;
  }

  const start = (safePage - 1) * size;
  const end = start + size;
  return {
    items: list.slice(start, end),
    totalPages,
    page: safePage,
  };
}

/**
 * Sort orders by `created_at` descending (newest first), stable.
 * Orders missing `created_at` sort to the end.
 *
 * Does NOT mutate the input.
 *
 * @param {Array<{created_at?: string}>} orders
 * @returns {Array}
 */
export function sortOrdersNewestFirst(orders) {
  const list = Array.isArray(orders) ? orders.slice() : [];
  // Decorate-sort-undecorate to guarantee stability across engines (V8's
  // Array.prototype.sort is stable since 2019, but being explicit makes the
  // intent obvious and survives future engine swaps).
  return list
    .map((order, index) => ({ order, index }))
    .sort((a, b) => {
      const aTime = a.order && a.order.created_at
        ? Date.parse(a.order.created_at)
        : NaN;
      const bTime = b.order && b.order.created_at
        ? Date.parse(b.order.created_at)
        : NaN;
      const aMissing = Number.isNaN(aTime);
      const bMissing = Number.isNaN(bTime);
      if (aMissing && bMissing) return a.index - b.index;
      if (aMissing) return 1;
      if (bMissing) return -1;
      if (bTime !== aTime) return bTime - aTime; // descending
      return a.index - b.index; // stable tiebreak
    })
    .map((wrapped) => wrapped.order);
}

/**
 * The STORE's order number for display ("AC-2026-01001").
 *
 * Distinct from ``getOrderId``, which returns the internal database key.
 * The two are NOT interchangeable: the id is what the route, the detail
 * fetch and the Vue list key are built on, and the order number is the only
 * one a shopper can quote to support. Rendering the id where the order
 * number belongs -- worse, with a "#" in front so it looks like one -- is
 * the bug this replaces.
 *
 * Returns null when the order has no number yet. ``orders.order_number`` is
 * nullable and a NULL is a legible "not yet assigned" marker
 * (models/order.py:226); falling back to the internal id is precisely what
 * produced the defect, so the caller renders NOTHING instead. An empty
 * string is treated as absent for the same reason.
 *
 * @param {object|null|undefined} order
 * @returns {string|null}
 */
export function getOrderNumber(order) {
  if (!order || typeof order !== "object") return null;
  return order.order_number || null;
}

/**
 * The amount the shopper was actually CHARGED for an order.
 *
 * ``orders.charged_amount`` is the FROZEN Stripe capture value and is
 * documented (``models/order.py``:278) as having to keep meaning "what the
 * buyer was actually charged". It already has the free-item discount folded
 * in -- ``models/account.py`` computes it from
 * ``free_item_discount_cents`` (:503 -> :505) -- and it is written at order
 * CREATION for the deferred/batched path as well as the charged one
 * (``charged_amount=cart_charge``, :3069), so it is populated before a
 * batched order is captured.
 *
 * Falls back to ``total`` when the column is absent: it is nullable and
 * legacy rows pre-date it, and rendering "$0.00" for a real order would be
 * a worse lie than rendering the pre-discount total.
 *
 * @param {object|null|undefined} order
 * @returns {number|string|null}
 */
export function getOrderChargedAmount(order) {
  if (!order || typeof order !== "object") return null;
  const charged = order.charged_amount;
  if (charged != null && Number.isFinite(Number(charged))) return charged;
  return order.total != null ? order.total : null;
}
